import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { BillingCategoryType } from 'propTypes';
import LinkButton from 'components/LinkButton/LinkButton';
import FALabel from 'components/shared/FALabel';
import ModalRoot from 'components/ModalRoot';
import ModalComponent from 'components/ModalComponent';
import EditableAttribute from 'components/shared/EditableAttribute';
import { billingCategoryGroups, modals } from 'ducks';
import ReorganizeCategoryModal from './ReorganizeCategoryModal';
import FontAwesome from 'react-fontawesome';
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import BillingCategoryRevenue from 'components/billingCategories/BillingCategoryRevenue';

const SubcategoryActions = connect()((props) => {
  const {
    dispatch,
    category,
    dirty,
    onCancel,
    onSave,
    onToggleActive,
    onToggleEdit,
    expanded,
  } = props;

  const activatable = category.id && category.parent_id;
  const moveable = !!category.id && (!!category.parent_id || category.children.length === 0);
  const editable = category.id && category.parent_id;

  const onReorganize = () => {
    dispatch(modals.actions.show('ReorganizeCategoryModal', { category }));
  };

  return (
    <div className='subcategory-actions'>
      {dirty && (
        <LinkButton
          onClick={onCancel}
          content={<FALabel icon='minus-circle' label='Cancel' className='text-danger action' />}
        />
      )}
      {dirty && (
        <LinkButton
          onClick={onSave}
          content={<FALabel icon='check-circle' label='Save' className='action' />}
        />
      )}
      {activatable && (
        <LinkButton
          className='btn-activate'
          onClick={onToggleActive}
          content={(
            <FALabel
              icon={category.active ? 'eye-slash' : 'eye'}
              label={category.active ? 'Make Inactive' : 'Activate'}
              className={category.active ? 'text-danger action' : 'action'}
            />
          )}
        />
      )}
      {moveable && (
        <LinkButton
          className='btn-move'
          onClick={onReorganize}
          content={<FALabel icon='sitemap' label='Move' className='action' />}
        />
      )}
      <LinkButton
        onClick={onToggleEdit}
        className={"edit-" + category.id}
        content={
          expanded
            ? <FALabel icon='chevron-up' label='Close' />
            : <FALabel icon='chevron-down' label='Edit' />
        }
      />
    </div>
  );
});

SubcategoryActions.propTypes = {
  category: BillingCategoryType.isRequired,
  dirty: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired
};

const DragHandle = SortableHandle(() => (
  <span className='drag-handle'>
    <FontAwesome name='reorder'/>
  </span>
));

const SubCategoryRow = (props) => {
  const {
    category,
    confirmDeactivate,
    onCancel,
    onSave,
    toggleActive,
    sortable
  } = props;

  const [dirty, setDirty] = useState(!category.id);
  const [editProxy, setEditProxy] = useState(Object.assign({}, category));
  const [showRevenue, setShowRevenue] = useState(false); // State to manage visibility

  const onToggleActive = () => {
    if (category.active) {
      // confirm deactivate
      const title = 'Confirmation';
      const message = (
        <div>
          <p>{`By marking this category "${category.name}" as inactive, the following will occur:`}</p>
          <ul>
            <li>Previous ledger items will remain accessible.</li>
            <li>The category will be removed from the quick action modals and filters.</li>
            <li>Contracts with fees that reference inactive categories cannot be submitted.</li>
          </ul>
          <h1>Important</h1>
          <p>Please update any current contract fees associated to this category.</p>
        </div>
      );
      const onConfirm = () => toggleActive(category);

      confirmDeactivate(message, title, onConfirm);
    } else {
      // activate
      toggleActive(category);
    }
  };
  const DragHandler = () => (sortable ? <DragHandle/> : <span/>);

  const onShowRevenue = () => {
    setShowRevenue(!showRevenue)
  }

  return (
    <>
    <tr>
      <td><DragHandler/></td>
      <td className='subcategory-name'>
        <EditableAttribute
          attribute='name'
          entity={editProxy}
          required
          onBeginEdit={() => setDirty(true)}
          onChange={(attribute, value) => setEditProxy(Object.assign(editProxy, { [attribute]: value }))}
        />
      </td>
      <td>{category.category_type === 'charge' ? 'Charge' : 'Credit'}</td>
      <td>
        <EditableAttribute
          attribute='account'
          entity={editProxy}
          onBeginEdit={() => setDirty(true)}
          onChange={(attribute, value) => setEditProxy(Object.assign(editProxy, { [attribute]: value }))}
        />
      </td>
      <td>
        <EditableAttribute
          attribute='adjustment_code'
          entity={editProxy}
          onBeginEdit={() => setDirty(true)}
          onChange={(attribute, value) => setEditProxy(Object.assign(editProxy, { [attribute]: value }))}
        />
      </td>
      <td>
        <EditableAttribute
          attribute='ledger_account_code'
          entity={editProxy}
          onBeginEdit={() => setDirty(true)}
          onChange={(attribute, value) => setEditProxy(Object.assign(editProxy, { [attribute]: value }))}
        />
      </td>
      <td>
        <SubcategoryActions
          category={category}
          dirty={dirty}
          onCancel={() => {
            setEditProxy(Object.assign({}, category));
            setDirty(false);
            onCancel();
          }}
          onSave={() => {
            setDirty(false);
            onSave(editProxy).catch(() => setDirty(true));
          }}
          onToggleActive={onToggleActive}
          onToggleEdit={onShowRevenue}
          expanded={showRevenue}
        />
      </td>
    </tr>
      {showRevenue && ( // Conditionally render BillingCategoryRevenue
        <tr>
          <td colSpan="7">
            <BillingCategoryRevenue
              category={category}
            />
          </td>
        </tr>
      )}
    </>
  );
};

SubCategoryRow.propTypes = {
  category: BillingCategoryType.isRequired,
  confirmDeactivate: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  sortable: PropTypes.bool,
  toggleActive: PropTypes.func,
};

SubCategoryRow.defaultProps = {
  confirmDeactivate: () => {},
  onCancel: () => {},
  onSave: () => {},
  sortable: false,
  toggleActive: () => {},
};

const SortableSubCategoryRow = SortableElement(props =>
  <SubCategoryRow {...props}/>
);

SortableSubCategoryRow.propTypes = {
  ...SubCategoryRow.propTypes
}

const collectionNumber = (cat) => {
  if (cat.active) {
    return cat.category_type === 'charge' ? 0 : 1;
  } else {
    return 2;
  }
};

const SortableSubCategories = SortableContainer(({
  categories,
  confirmDeactivate,
  onCancel,
  onSave,
  toggleActive
}) => (
  <tbody>
  {categories.map((cat, index) => (
    <SortableSubCategoryRow
      key={cat.id}
      category={cat}
      confirmDeactivate={confirmDeactivate}
      onCancel={onCancel}
      onSave={onSave}
      toggleActive={toggleActive}

      // SortableElement props:
      index={index}
      collection={collectionNumber(cat)}
      disabled={cat.parent_id == null || !cat.active}
      sortable={cat.parent_id != null && cat.active}
    />
  ))}
  </tbody>
));

SortableSubCategories.propTypes = {
  categories: PropTypes.arrayOf(BillingCategoryType).isRequired,
  confirmDeactivate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  toggleActive: PropTypes.func.isRequired
}

const CategoryAuditSelector = ({ categoryGroup, showHistory }) => (
  <ModalComponent title='Select Subcategory'>
    <p>Select the subcategory to view edit history:</p>
    <ul className='list-unstyled'>
      <li>
        <LinkButton content={categoryGroup.name} onClick={() => showHistory(categoryGroup.id)} />
      </li>
      {categoryGroup.children.map((category) => (
        <li key={category.id}>
          <LinkButton content={category.name} onClick={() => showHistory(category.id)} />
        </li>
      ))}
    </ul>
  </ModalComponent>
);

CategoryAuditSelector.propTypes = {
  categoryGroup: BillingCategoryType.isRequired,
  showHistory: PropTypes.func.isRequired
};

const BillingCategoryDetails = (props) => {
  const {
    category: categoryGroup,
    createCategory,
    updateCategory,
    toggleActive,
    confirmDeactivate,
    reorderCategories,
    showHistoryChooser,
    showHistory
  } = props;
  const [drafting, setDrafting] = useState(null);
  const [status, setStatus] = useState(null);

  const onCancel = () => {
    setDrafting(null);
    setStatus(null);
  };

  const onSave = (category) => {
    const saveHandler = category.id ? updateCategory : createCategory;
    return saveHandler(category)
      .then(() => {
        setDrafting(null);
        setStatus({
          type: 'success',
          message: `Category "${category.name}" saved!`
        });
      })
      .catch((resp) => {
        setStatus({ type: 'error', message: resp.errors });
        throw resp;
      });
  };

  const viewHistory = () => {
    if (categoryGroup.children.length > 0) {
      showHistoryChooser(categoryGroup, showHistory);
    } else {
      showHistory(categoryGroup.id);
    }
  };

  const onToggleAppliesToStudent = () => {
    updateCategory(Object.assign({}, categoryGroup, { contact: !categoryGroup.contact }))
      .catch(() => setStatus({
        type: 'error',
        message: 'Apply to Student Balance cannot be changed due to existing billing ledger entries.'
      }));
  };

  const onTogglePrepayExcluded = () => {
    updateCategory(Object.assign({}, categoryGroup, { prepay_excluded: !categoryGroup.prepay_excluded }))
      .catch(() => setStatus({
        type: 'error',
        message: 'Something went wrong.'
      }));
  };

  const subcategories = categoryGroup.children.sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    } else if (a.category_type !== b.category_type) {
      return a.category_type === 'charge' ? -1 : 1;
    } else {
      // Fallback to default ordering
      return 0;
    }
  });

  const containerRef = React.createRef();

  return (
    <div className='detail-panel'>
      <div className='details-group-actions card bg-light'>

        <div className={'category-group-options'}>
          <LinkButton
            className='category-group-option btn-apply-student-balance'
            onClick={onToggleAppliesToStudent}
            content={(
              <FALabel
                iconFirst
                icon={categoryGroup.contact ? 'check-square' : 'square-o'}
                label='Apply To Student Balance'
              />
            )}
          />

          <LinkButton
            className='category-group-option btn-prepay-excluded'
            onClick={onTogglePrepayExcluded}
            content={(
              <FALabel
                iconFirst
                icon={categoryGroup.prepay_excluded ? 'check-square' : 'square-o'}
                label='Excluded From Automatic Overpayment Logic'
              />
            )}
          />
        </div>

        <LinkButton
          onClick={viewHistory}
          content={<FALabel label='View Edit History' icon='history' />}
        />

      </div>
      { status && (
        <div className={`alert alert-${status.type === 'error' ? 'danger' : 'info'}`}>
          {status.message}
        </div>
      )}
      <table className='table subcategories-table' ref={containerRef}>
        <thead>
          <tr>
            <th/>
            <th>Name</th>
            <th className='subcategory-type'>Type</th>
            <th>Account / GL ID</th>
            <th>Adjustment Code</th>
            <th>Ledger Account Code</th>
            <th className='subcategory-actions' />
          </tr>
        </thead>

        <SortableSubCategories
          categories={[categoryGroup, ...subcategories]}
          confirmDeactivate={confirmDeactivate}
          onCancel={onCancel}
          onSave={onSave}
          toggleActive={toggleActive}

          // SortableContainer props:
          useDragHandle={true}
          onSortEnd={({ newIndex, oldIndex }) => {
            if (newIndex !== oldIndex) {
              reorderCategories(
                categoryGroup,
                arrayMove(subcategories, oldIndex - 1, newIndex - 1)
              );
            }
          }}
          helperClass='sortable-dragging'
          helperContainer={() => containerRef.current}
          helperContainerRef={containerRef}
        />

        {
          drafting && (
            <tbody>
            <SubCategoryRow
              category={{ parent_id: categoryGroup.id, active: true, category_type: drafting }}
              onCancel={onCancel}
              onSave={onSave}
            />
            </tbody>
          )
        }
      </table>

      <LinkButton
        className='add-item-button'
        onClick={() => setDrafting('charge')}
        content={<FALabel icon='plus-circle' iconFirst label='Add New Charge Category' />}
      />

      <LinkButton
        className='add-item-button'
        onClick={() => setDrafting('credit')}
        content={<FALabel icon='plus-circle' iconFirst label='Add New Credit Category' />}
      />

      <ModalRoot modals={{ CategoryAuditSelector, ReorganizeCategoryModal }} />
    </div>
  );
};

BillingCategoryDetails.propTypes = {
  category: BillingCategoryType.isRequired,
  createCategory: PropTypes.func.isRequired,
  toggleActive: PropTypes.func.isRequired,
  updateCategory: PropTypes.func.isRequired,
  confirmDeactivate: PropTypes.func.isRequired,
  reorderCategories: PropTypes.func.isRequired,
  showHistoryChooser: PropTypes.func.isRequired,
  showHistory: PropTypes.func.isRequired,
  setSaveError: PropTypes.func
};

const mapDispatchToProps = (dispatch) => ({
  createCategory: (category) => (
    dispatch(billingCategoryGroups.actions.create(category))
      .then(() => dispatch(billingCategoryGroups.actions.markDirty('_active')))
  ),
  updateCategory: (category) => (
    dispatch(billingCategoryGroups.actions.update(category))
      .then(() => dispatch(billingCategoryGroups.actions.markDirty('_active')))
  ),
  toggleActive: (category) => (
    dispatch(billingCategoryGroups.actions.update(Object.assign(category, { active: !category.active })))
      .then(() => dispatch(billingCategoryGroups.actions.markDirty('_active', '_inactive')))
  ),
  confirmDeactivate: (message, title, onConfirm) => (
    dispatch(modals.actions.show('VerificationModal', { message, title, onConfirm }))
  ),
  showHistoryChooser: (categoryGroup, showHistory) => (
    dispatch(modals.actions.show('CategoryAuditSelector', { categoryGroup, showHistory }))
  ),
  showHistory: (id) => (
    dispatch(modals.actions.show('AuditViewer', { id, resource: 'billing_categories' }))
  ),
  reorderCategories: (categoryGroup, subcategories) => {
    // We are reordering the "children" property of a category group entity. Since this is a non-standard
    // usage of DuckEntity `reorder` it doesn't know how to update state so we have to manually do it.
    dispatch(billingCategoryGroups.actions.cacheSingle({...categoryGroup, children: subcategories}));
    return dispatch(
      billingCategoryGroups.actions.reorder(
        subcategories.map(c => c.id),
        // Use an arbitrary state key to avoid overwriting state
        `dummy_reorder_${categoryGroup.id}`
      )
    )
      .catch(() => {
        // revert state change
        dispatch(billingCategoryGroups.actions.cacheSingle(categoryGroup));
        return dispatch(modals.actions.show('UnexpectedError'));
      });
  },
});

export default connect(null, mapDispatchToProps)(BillingCategoryDetails);
